import {
	Box,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useRef, useState } from "react";
import { DelayByShift } from "../models/DelayByShift";

export const DelaysCategoryBreakdown = ({
	delays,
	sectionName,
}: {
	delays: DelayByShift[];
	sectionName?: string | null;
}) => {
	const chartRef = useRef<ReactECharts>(null);
	const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

	const categoryData = (() => {
		const categoryMap = new Map<
			string,
			{ color: string; duration: number; count: number }
		>();
		let totalDuration = 0;

		for (const delay of delays) {
			const catName = delay.DelayCategoryName || "Undeclared";
			const catColor = delay.DelayCategoryColor || "#9e9e9e";
			totalDuration += delay.Duration || 0;

			const existing = categoryMap.get(catName);
			if (existing) {
				existing.duration += delay.Duration || 0;
				existing.count += 1;
			} else {
				categoryMap.set(catName, {
					color: catColor,
					duration: delay.Duration || 0,
					count: 1,
				});
			}
		}

		return Array.from(categoryMap.entries())
			.map(([name, data]) => ({
				name,
				color: data.color,
				durationSec: data.duration,
				durationMin: data.duration / 60,
				durationHrs: data.duration / 3600,
				eventCount: data.count,
				percentage:
					totalDuration > 0 ? (data.duration / totalDuration) * 100 : 0,
			}))
			.sort((a, b) => b.durationMin - a.durationMin);
	})();

	const handleChartEvents = {
		mouseover: (params: { dataIndex: number }) =>
			setHighlightedIndex(params.dataIndex),
		mouseout: () => setHighlightedIndex(null),
	};

	const handleTableRowHover = (index: number | null) => {
		setHighlightedIndex(index);
		const chartInstance = chartRef.current?.getEchartsInstance();
		if (chartInstance) {
			if (index !== null) {
				chartInstance.dispatchAction({
					type: "highlight",
					seriesIndex: 0,
					dataIndex: index,
				});
				chartInstance.dispatchAction({
					type: "showTip",
					seriesIndex: 0,
					dataIndex: index,
				});
			} else {
				chartInstance.dispatchAction({ type: "downplay", seriesIndex: 0 });
				chartInstance.dispatchAction({ type: "hideTip" });
			}
		}
	};

	const chartOption = {
		title: {
			text: "Delay Time by Category",
			left: "center",
			textStyle: { fontSize: 13, fontWeight: "bold" },
		},
		tooltip: {
			trigger: "item",
			formatter: (params: { name: string; value: number; percent: number }) =>
				`${params.name}<br/>Duration: ${params.value} min (${params.percent}%)`,
		},
		legend: {
			orient: "vertical",
			right: 10,
			top: "middle",
			data: categoryData.map((cat) => cat.name),
		},
		series: [
			{
				name: "Category",
				type: "pie",
				radius: ["0%", "70%"],
				center: ["40%", "55%"],
				data: categoryData.map((cat) => ({
					value: Math.round(cat.durationMin * 10) / 10,
					name: cat.name,
					itemStyle: { color: cat.color },
				})),
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
						shadowOffsetX: 0,
						shadowColor: "rgba(0,0,0,0.5)",
					},
				},
				label: {
					show: true,
					formatter: (params: { value: number; percent: number }) =>
						`${params.value} (${params.percent}%)`,
					position: "outside",
				},
				labelLine: { show: true },
			},
		],
	};

	return (
		<Paper variant="outlined" sx={{ mb: 1 }}>
			<Box
				sx={{
					px: 2,
					py: 1,
					backgroundColor: (theme) => theme.palette.primary.main,
				}}
			>
				<Typography variant="subtitle2" sx={{ color: "white" }}>
					{sectionName ? `${sectionName} — ` : ""}Delay Breakdown by Category
				</Typography>
			</Box>

			<Divider />

			<Box sx={{ display: "flex" }}>
				<Box
					sx={{
						flex: 4,
						borderRight: "1px solid",
						borderColor: "divider",
						overflow: "hidden",
					}}
				>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell rowSpan={2}>Category</TableCell>
								<TableCell
									colSpan={2}
									align="center"
									sx={{ borderBottom: "none" }}
								>
									Duration
								</TableCell>
								<TableCell rowSpan={2} align="right">
									Events
								</TableCell>
								<TableCell rowSpan={2} align="right">
									% of Total
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell
									align="right"
									sx={{
										color: "text.secondary",
										fontSize: "0.7rem",
										borderTop: "1px #d0d0d0 solid",
									}}
								>
									minutes
								</TableCell>
								<TableCell
									align="right"
									sx={{
										color: "text.secondary",
										fontSize: "0.7rem",
										borderTop: "1px #d0d0d0 solid",
									}}
								>
									hours
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{categoryData.map((row, index) => (
								<TableRow
									key={row.name}
									hover
									onMouseEnter={() => handleTableRowHover(index)}
									onMouseLeave={() => handleTableRowHover(null)}
									sx={{
										cursor: "pointer",
										...(highlightedIndex === index
											? { backgroundColor: `${row.color}30` }
											: {}),
									}}
								>
									<TableCell>
										<Box
											sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
										>
											<Box
												sx={{
													width: 8,
													height: 8,
													borderRadius: "50%",
													backgroundColor: row.color,
													flexShrink: 0,
												}}
											/>
											<Typography variant="body2" fontWeight={500}>
												{row.name}
											</Typography>
										</Box>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2">
											{row.durationMin.toFixed(1)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2">
											{row.durationHrs.toFixed(2)}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2">{row.eventCount}</Typography>
									</TableCell>
									<TableCell align="right">
										<Typography variant="body2" color="text.secondary">
											{row.percentage.toFixed(1)}%
										</Typography>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>

				<Box sx={{ flex: 6, minHeight: 350, p: 1 }}>
					<ReactECharts
						ref={chartRef}
						option={chartOption}
						onEvents={handleChartEvents}
						style={{ height: "100%", minHeight: 350 }}
					/>
				</Box>
			</Box>
		</Paper>
	);
};
